import Cache from './cache';

export default class Utils {
	static dataSelector = 'data-lazy-settings'; // Holds all visibility data from elementor controls
	static hideClass = 'elementor-hidden'; // CSS class that hides the elements on the DOM
	static lazyHandled = 'data-lazy-handled'; // flag for lazy load elements that are initiated

	/**
	 * get visibility data
	 *
	 * @param {HTMLElement} element
	 * @returns {any}
	 */
	static getData( element ) {
		return JSON.parse( element.getAttribute( Utils.dataSelector ) );
	}

	/**
	 * console log error
	 * @param error
	 */
	static logError( error ) {
		Utils.log( error, 'error' );
	}

	/**
	 * console log utility
	 * @param obj
	 * @param {string} logType
	 */
	static log( obj, logType = 'info' ) {
		if ( undefined === typeof console[ logType ] ) {
			logType = 'info';
		}
		console[ logType ]( 'Widget LazyLoad: ', obj );
	}

	/**
	 * check if an asset was already loaded
	 * @param {string} assetURL
	 * @param {string} type
	 * @returns {boolean}
	 */
	static assetExist( assetURL = '', type = 'js' ) {
		if ( 'js' === type ) {
			return null !== document.querySelector( `script[src^="${assetURL}"]` );
		}
		return null !== document.querySelector( `link[href^="${assetURL}"]` )
	}

	/**
	 * load js if needed
	 * @param {string} jsAssetURL
	 */
	static enqueueJS( jsAssetURL ) {
		// is it loaded already?
		if ( Utils.assetExist( jsAssetURL ) ) {
			return;
		}
		const script = document.createElement( 'script' );
		script.type = 'text/javascript';
		script.src = jsAssetURL;
		document.body.appendChild( script );
	}

	/**
	 * load css if needed
	 * @param {string} cssAssetURL
	 */
	static enqueueCSS( cssAssetURL ) {
		if ( Utils.assetExist( cssAssetURL, 'css' ) ) {
			return;
		}
		const link = document.createElement( 'link' );
		link.rel = ' stylesheet'
		link.type = 'text/css';
		link.href= cssAssetURL;
		document.head.appendChild( link );
	}

	/**
	 * load assets to the page if needed
	 * @param {string[]} assets
	 */
	static enqueueAssets( assets ) {
		assets.forEach( asset => {
			let type = asset.split( '/' );
			type = type[ type.length - 1 ];
			if ( type.toLowerCase().includes( '.js' ) ) {
				Utils.enqueueJS( asset );
			} else {
				Utils.enqueueCSS( asset )
			}
		} );
	}

	/**
	 * fetch remote element
	 * @param {int} templateID
	 * @param {string} widgetID
	 * @param {object} data
	 * @returns {Promise<{}|*|Response>}
	 */
	static async fetchElement( templateID = 0, widgetID = '', data = {} ) {
		if ( 0 === templateID || '' === widgetID ) {
			return {};
		}
		const {
			useCache = false,
			cacheTTL = 30,
		} = data;

		const cacheKey = `${ templateID }-${ widgetID }`
		const { widgetBase } = window.eWidgetLazyload || { widgetBase: '/e-widgets-api/widget/' }
		const fetchUrl = `${ widgetBase }${ cacheKey }/`;

		if ( useCache && Cache.existsAndValid( cacheKey ) ) {
			return Cache.get( cacheKey );
		}

		try {
			let response = await fetch( fetchUrl );
			const responseJson = await response.json();
			if ( ! responseJson || ! responseJson.success ) {
				// noinspection ExceptionCaughtLocallyJS
				throw new Error( responseJson?.data || responseJson );
			}
			if ( useCache ) {
				Cache.set( cacheKey, responseJson.data, cacheTTL );
			}
			return responseJson.data;
		} catch ( error ) {
			Utils.logError( error );
		}
	}

	/**
	 * Debounce a callback to make sure it only runs once in a given time frame
	 * @param func
	 * @param timeout
	 * @returns {(function(...[*]=): void)|*}
	 */
	static debounce( func, timeout = 300 ){
		let timer;
		return ( ...args ) => {
			clearTimeout( timer );
			timer = setTimeout( () => {
				func.apply( this, args );
			}, timeout );
		};
	}
}
